/**
 * Usuario tal como lo maneja el backend (roles: 0 ADMIN, 1 USER, 2 SELLER).
 * Cada app tiene sus cuentas: una cuenta tiene UN rol (`roleId`) y el mismo
 * correo puede tener una en cada app.
 */
export interface AdminUser {
  id: number;
  firstName?: string;
  lastName?: string;
  name?: string;
  email: string;
  phone?: string;
  phoneVerifiedAt?: string | null;
  roleId?: number;
  role_id?: number;
  /** Siempre `[roleId]` (compatibilidad). Backends anteriores no lo mandan. */
  roleIds?: number[];
  status?: string;
  isActive?: boolean | number;
  is_active?: boolean | number;
  /** Calificación efectiva (0–5) que ve la app. */
  rating?: number | null;
  /** Si no es null, un admin la fijó a mano y el promedio no la sobrescribe. */
  ratingOverride?: number | null;
  /** URL de la foto de perfil (un avatar del catálogo o una foto subida). */
  userPhoto?: string | null;
  createdAt?: string;
  created_at?: string;
}

/** Calificación numérica de un usuario, o `null` si el backend no la trae. */
export function userRating(u: Partial<AdminUser> | null | undefined): number | null {
  const raw = Number(u?.rating);
  return Number.isFinite(raw) ? raw : null;
}

/** `true` si la calificación fue ajustada a mano por un administrador. */
export function userRatingIsManual(u: Partial<AdminUser> | null | undefined): boolean {
  return u?.ratingOverride != null;
}

/** Nombre presentable de un usuario, tolerante a distintos formatos del backend. */
export function userDisplayName(u: Partial<AdminUser> | null | undefined): string {
  if (!u) return '—';
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return u.name || full || u.email || `#${u.id ?? '?'}`;
}

export function userRoleId(u: Partial<AdminUser> | null | undefined): number {
  return Number(u?.roleId ?? u?.role_id ?? 1);
}

/** Todos los roles del usuario; si el backend no manda `roleIds`, solo el principal. */
export function userRoleIds(u: Partial<Pick<AdminUser, 'roleIds' | 'roleId' | 'role_id'>> | null | undefined): number[] {
  if (Array.isArray(u?.roleIds) && u.roleIds.length) return [...u.roleIds].map(Number).sort((a, b) => a - b);
  return [userRoleId(u)];
}

export function userIsActive(u: Partial<AdminUser> | null | undefined): boolean {
  const raw = u?.isActive ?? u?.is_active;
  if (raw === undefined || raw === null) return (u?.status || '').toUpperCase() !== 'SUSPENDED';
  return raw === true || raw === 1;
}

/**
 * Dirección de un usuario. El backend (`GET /users/:id/addresses`) usa nombres
 * en español; se conservan los alias en inglés por compatibilidad.
 */
export interface UserAddress {
  id: number;
  recipientName?: string;
  phone?: string;
  street?: string;
  exteriorNumber?: string;
  interiorNumber?: string;
  colonia?: string;
  municipio?: string;
  estado?: string;
  codigoPostal?: string;
  referencias?: string;
  isPrimary?: boolean | number;
  extNumber?: string;
  intNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isDefault?: boolean | number;
  [key: string]: unknown;
}

/** Avatar predefinido del catálogo (`GET /users/avatars`); la imagen vive en el bucket. */
export interface AvatarOption {
  id: string;
  url: string;
  gender: 'male' | 'female';
  age: 'young' | 'adult' | 'senior';
  role: 'mecanico' | 'vendedor' | 'comprador' | 'empresario' | 'almacen';
}

/** Iniciales para cuando el usuario no tiene foto. */
export function userInitials(u: Partial<AdminUser> | null | undefined): string {
  const letters = [u?.firstName, u?.lastName].map((p) => (p || '').trim().charAt(0)).join('');
  return (letters || (u?.email || '?').charAt(0)).toUpperCase();
}

/** Sesión / dispositivo con token activo (`GET /auth/devices`). */
export interface DeviceSession {
  id?: number;
  deviceId?: string;
  deviceName?: string;
  model?: string;
  platform?: string;
  ip?: string;
  lastUsedAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** Registro de la bitácora de inicios de sesión (spec 1.5). */
export interface LoginRecord {
  id?: number;
  device?: string;
  model?: string;
  ip?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** Datos mínimos del usuario autenticado que se guardan en sesión. */
export interface AuthUser {
  id: number;
  name: string;
  email: string;
  roleId: number;
  /** Sesiones guardadas antes de los roles múltiples no lo traen. */
  roleIds?: number[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: AuthUser;
  token: string;
}
